import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './GameModal.css';
import { gameAPI } from '../../services/api';
import { useStore } from '../../store/useStore';
import SoundManager from '../../services/SoundManager';

interface GameModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameId?: string;
}

interface GameInfo {
  id: string;
  name: string;
  description: string;
  cost: number;
  minReward: number;
  maxReward: number;
  winChance: number;
  cooldown: number;
  isAvailable: boolean;
  canPlay: boolean;
  cooldownLeft: number;
  canAfford: boolean;
  config?: any; // Конфиг с шансами для cosmic_dice, meteor_race и др.
}

interface AttemptInfo {
  freeAttemptsRemaining: number;
  freeAttemptsPerDay: number;
  paidAttemptsToday: number;
  totalAttemptsToday: number;
  maxPaidAttempts: number;
  maxAttemptsReached: boolean;
  nextAttemptCost: number;
  isFreeAttempt: boolean;
  canAfford: boolean;
  userBalance: number;
  activeBoost?: {
    active: boolean;
    multiplier?: number;
    sourceGame?: string;
    remainingSeconds?: number;
    expiresAt?: string;
  };
}

interface Rocket {
  id: number;
  emoji: string;
  color: string;
  name: string;
  chance: number;
  boost: number;
  duration: number;
}

const GameModal: React.FC<GameModalProps> = ({ isOpen, onClose, gameId }) => {
  const { t } = useTranslation();
  const { user, updateBalance } = useStore();
  const [game, setGame] = useState<GameInfo | null>(null);

  // Маппинг game.id -> ключ локализации
  const getLocalizedGameName = (gameId: string): string => {
    const gameNameMap: Record<string, string> = {
      'lunar_lottery': t('games.lunarLottery.title'),
      'star_flip': t('games.starFlip.title'),
      'cosmic_dice': t('games.cosmicDice.title'),
      'meteor_race': t('games.meteorRace.title'),
      'asteroid_miner': t('games.asteroidMiner.title'),
      'star_lottery': t('games.starLottery.title')
    };
    return gameNameMap[gameId] || gameId;
  };
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');
  const [choice, setChoice] = useState<any>(null);
  const [attemptInfo, setAttemptInfo] = useState<AttemptInfo | null>(null);
  const [loadingAttemptInfo, setLoadingAttemptInfo] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [rockets, setRockets] = useState<Rocket[]>([]);

  useEffect(() => {
    if (isOpen && gameId) {
      SoundManager.playModalOpen();
      loadGameInfo();
      loadAttemptInfo();

      // Загружаем конфигурацию для meteor_race
      if (gameId === 'meteor_race') {
        loadRocketsConfig();
      }
    }
  }, [isOpen, gameId]);

  const loadGameInfo = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await gameAPI.getAllGames();

      if (response.data.success) {
        const currentGame = response.data.games.find((g: GameInfo) => g.id === gameId);
        setGame(currentGame || null);
      }
    } catch (error) {
      console.error('Error loading game:', error);
      setError(t('modals.gameModal.errorLoad'));
    } finally {
      setLoading(false);
    }
  };

  const loadAttemptInfo = async () => {
    if (!gameId) return;

    try {
      setLoadingAttemptInfo(true);
      const response = await gameAPI.getAttemptInfo(gameId);

      if (response.data.success) {
        console.log('🎮 Обновлена информация о попытках:', response.data.data);
        setAttemptInfo(response.data.data);
      }
    } catch (error) {
      console.error('Error loading attempt info:', error);
    } finally {
      setLoadingAttemptInfo(false);
    }
  };

  const loadRocketsConfig = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://moonraid.online/api';
      const response = await fetch(`${API_BASE_URL}/game-configs/meteor_race`);
      const data = await response.json();

      if (data.success && data.config.rockets) {
        // Преобразуем данные из БД в формат для UI
        const rocketsData = data.config.rockets.map((r: any) => ({
          id: r.id,
          emoji: r.emoji,
          color: r.color,
          name: t(`games.meteorRace.rockets.${r.name}`),
          chance: Math.round(r.chance * 100), // Конвертируем 0.6 → 60
          boost: Math.round((r.multiplier - 1) * 100), // Конвертируем 1.3 → 30
          duration: r.duration
        }));
        setRockets(rocketsData);
        console.log('✅ Конфигурация ракет загружена из БД:', rocketsData);
      }
    } catch (error) {
      console.error('❌ Ошибка загрузки конфигурации ракет:', error);
      // Используем дефолтные значения при ошибке
      setRockets([
        { id: 1, emoji: '🚀', color: '#FF6B6B', name: t('games.meteorRace.rockets.redLightning'), chance: 60, boost: 30, duration: 45 },
        { id: 2, emoji: '🛸', color: '#4ECDC4', name: t('games.meteorRace.rockets.blueWhirlwind'), chance: 40, boost: 100, duration: 80 },
        { id: 3, emoji: '🛰️', color: '#45B7D1', name: t('games.meteorRace.rockets.spaceWanderer'), chance: 25, boost: 200, duration: 150 },
        { id: 4, emoji: '✨', color: '#FFA07A', name: t('games.meteorRace.rockets.starDust'), chance: 15, boost: 400, duration: 300 }
      ]);
    }
  };

  const playGame = async () => {
    // КРИТИЧЕСКИ ВАЖНО: Блокируем повторные вызовы пока игра идет!
    if (playing) {
      console.warn('❌ Попытка играть пока игра уже идет - блокируем!');
      return;
    }

    if (!game) {
      console.error('❌ playGame: game is null');
      return;
    }

    if (!game.canPlay) {
      console.error('❌ playGame: game.canPlay is false', {
        game: game.id,
        canPlay: game.canPlay,
        cooldownLeft: game.cooldownLeft,
        isAvailable: game.isAvailable
      });
      return;
    }

    // Звук начала игры
    SoundManager.playGameStart();

    // Вибрация при начале игры
    try {
      if ((window as any).Telegram?.WebApp?.HapticFeedback) {
        (window as any).Telegram.WebApp.HapticFeedback.impactOccurred('light');
      }
    } catch (e) {}

    try {
      setPlaying(true);
      setError('');
      setResult(null);

      // Сначала списываем попытку
      if (!gameId) {
        setError(t('modals.gameModal.errorGameIdUndefined'));
        setPlaying(false);
        return;
      }

      try {
        const attemptResponse = await gameAPI.useAttempt(gameId);
        if (!attemptResponse.data.success) {
          setError(attemptResponse.data.message || t('modals.gameModal.errorUseAttempt'));
          setPlaying(false);
          return;
        }

        // Обновляем баланс если была платная попытка
        if (!attemptResponse.data.data.usedFreeAttempt) {
          updateBalance(attemptResponse.data.data.newBalance);
        }

        // Обновляем информацию о попытках НАПРЯМУЮ из ответа (избегаем кэширования)
        console.log('🎮 Обновляем attemptInfo из ответа useAttempt:', attemptResponse.data.data);
        setAttemptInfo(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            freeAttemptsRemaining: attemptResponse.data.data.freeAttemptsRemaining,
            paidAttemptsToday: attemptResponse.data.data.paidAttemptsToday,
            totalAttemptsToday: attemptResponse.data.data.totalAttemptsToday,
            userBalance: attemptResponse.data.data.newBalance,
            nextAttemptCost: attemptResponse.data.data.nextAttemptCost,
            isFreeAttempt: attemptResponse.data.data.isFreeAttempt,
            maxPaidAttempts: attemptResponse.data.data.maxPaidAttempts,
            maxAttemptsReached: attemptResponse.data.data.maxAttemptsReached,
            canAfford: attemptResponse.data.data.canAfford
          };
        });
      } catch (attemptError: any) {
        console.error('Error using attempt:', attemptError);
        if (attemptError.response?.status === 400) {
          setError(attemptError.response.data.message || t('modals.gameModal.errorInsufficientFunds'));
        } else {
          setError(t('modals.gameModal.errorUseAttempt'));
        }
        setPlaying(false);
        return;
      }

      // Подготавливаем выбор в зависимости от игры
      let gameChoice = null;
      switch(game.id) {
        case 'lunar_lottery':
          // Используем выбор пользователя
          gameChoice = choice;
          break;
        case 'star_flip':
          // Используем выбор пользователя
          gameChoice = choice;
          break;
        case 'cosmic_dice':
          // Используем выбор пользователя
          gameChoice = choice;
          break;
        case 'meteor_race':
          // Используем выбор пользователя
          gameChoice = choice;
          break;
        case 'asteroid_miner':
          // Используем выбор пользователя
          gameChoice = choice;
          break;
        default:
          gameChoice = null;
      }

      // Показываем анимацию прогресса на 3 секунды
      setResult({ loading: true, message: t('modals.gameModal.playing') });
      
      // Имитируем прогресс игры
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Используем основной API который правильно сохраняет результаты
      const response = await gameAPI.playMiniGame({
        gameType: game.id,
        choice: gameChoice
      });

      if (response.data.success) {
        const gameResult = response.data.data;

        console.log('🎮 [GameModal] ========= ПОЛУЧЕН РЕЗУЛЬТАТ ИГРЫ =========');
        console.log('🎮 [GameModal] Полный объект result:', JSON.stringify(gameResult, null, 2));
        console.log('🎮 [GameModal] won:', gameResult.won, 'тип:', typeof gameResult.won);
        console.log('🎮 [GameModal] boost:', gameResult.boost);
        console.log('🎮 [GameModal] boost?.multiplier:', gameResult.boost?.multiplier);
        console.log('🎮 [GameModal] boost?.duration:', gameResult.boost?.duration);
        console.log('🎮 [GameModal] loading:', gameResult.loading);
        console.log('🎮 [GameModal] =======================================');

        // Вибрация при результате
        try {
          if ((window as any).Telegram?.WebApp?.HapticFeedback) {
            if (gameResult.won) {
              // Тройная вибрация при выигрыше
              for (let i = 0; i < 3; i++) {
                setTimeout(() => (window as any).Telegram.WebApp.HapticFeedback.impactOccurred('medium'), i * 100);
              }
            } else {
              // Одинарная вибрация при проигрыше
              (window as any).Telegram.WebApp.HapticFeedback.impactOccurred('heavy');
            }
          }
        } catch (e) {}

        console.log('🎮 [GameModal] Устанавливаем result в state через setResult()');
        setResult(gameResult);
        // ❌ НЕ ОБНОВЛЯЕМ БАЛАНС! Мини-игры бесплатны и не меняют основной баланс.
        // Баланс обновляется только при реальных операциях: claim, deposit, withdraw, платные попытки.
        // updateBalance(gameResult.newBalance);

        // Проигрываем звук победы или проигрыша
        if (gameResult.won) {
          SoundManager.playGameWin();
          console.log('🎵 [GameModal] Проигрываем звук победы');
        } else {
          SoundManager.playGameLoss();
          console.log('🎵 [GameModal] Проигрываем звук проигрыша');
        }

        // Если получен буст, запускаем обновление интерфейса
        if (gameResult.boost) {
          console.log('🎉 [GameModal] Получен буст из response:', gameResult.boost);
          // Отправляем событие о получении буста
          window.dispatchEvent(new CustomEvent('boostActivated', {
            detail: gameResult.boost
          }));
          console.log('📢 [GameModal] Событие boostActivated отправлено');
        } else {
          console.log('❌ [GameModal] Буст НЕ получен в response');
        }

        // НЕ сбрасываем result автоматически - пользователь должен видеть результат
        // Result сбросится при закрытии модалки или новой игре
        
        // Обновляем информацию об игре через 2 секунды
        setTimeout(() => {
          loadGameInfo();
        }, 2000);
      } else {
        setError(response.data.message || t('modals.gameModal.errorGame'));
      }
    } catch (error: any) {
      console.error('❌ Game error:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      // Более информативные сообщения об ошибках
      if (error.response?.status === 400) {
        setError(error.response.data.message || t('modals.gameModal.errorInsufficientOrUnavailable'));
      } else if (error.response?.status === 403) {
        // SESSION_WIN_LIMIT - пользователь уже выиграл в этой сессии
        const nextWin = error.response.data?.data?.nextWinAvailable;
        if (error.response.data?.error === 'SESSION_WIN_LIMIT') {
          // Форматируем время из ISO в человекочитаемый формат
          const timeFormatted = nextWin ? formatTimeUntil(nextWin) : '12ч';
          setError(t('modals.gameModal.sessionWinLimit', { time: timeFormatted }));
        } else {
          setError(error.response.data.message || t('modals.gameModal.errorSaveResult'));
        }
      } else if (error.response?.status === 429) {
        setError(error.response.data.message || t('modals.gameModal.errorCooldown'));
      } else if (error.response?.status === 404) {
        setError(t('modals.gameModal.errorUserNotFound'));
      } else {
        setError(t('modals.gameModal.errorSaveResult'));
      }
    } finally {
      setPlaying(false);
    }
  };

  const formatCooldown = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}ч ${minutes}м`;
    } else if (minutes > 0) {
      return `${minutes}м ${secs}с`;
    } else {
      return `${secs}с`;
    }
  };

  // Форматирование времени из ISO строки в человекочитаемый формат
  const formatTimeUntil = (isoDate: string): string => {
    try {
      const targetDate = new Date(isoDate);
      const now = new Date();
      const diffMs = targetDate.getTime() - now.getTime();

      if (diffMs <= 0) return '0м';

      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

      if (diffHours >= 1) {
        return `${diffHours}ч ${diffMinutes}м`;
      } else {
        return `${diffMinutes}м`;
      }
    } catch {
      return '12ч';
    }
  };

  const renderGameInterface = () => {
    if (!game) return null;

    switch(game.id) {
      case 'lunar_lottery':
        // Гарантируем что choice всегда массив для лотереи
        const lotteryChoice = Array.isArray(choice) ? choice : [];
        return (
          <div className="game-interface lottery">
            <h3>{t('games.lunarLottery.title')}</h3>
            <p>{t('games.lunarLottery.description')}</p>
            <div className="lottery-numbers">
              {[1,2,3,4,5,6,7,8,9,10].map(num => (
                <button
                  key={num}
                  className={`number-btn ${lotteryChoice.includes(num) ? 'selected' : ''}`}
                  onClick={() => {
                    SoundManager.playButtonClick();
                    if (lotteryChoice.includes(num)) {
                      // Убираем число если оно уже выбрано
                      setChoice(lotteryChoice.filter((n: number) => n !== num));
                    } else if (lotteryChoice.length < 3) {
                      // Добавляем если меньше 3
                      setChoice([...lotteryChoice, num]);
                    } else {
                      // Если уже 3 выбрано - заменяем первое на новое
                      setChoice([...lotteryChoice.slice(1), num]);
                    }
                  }}
                  disabled={playing}
                >
                  {num}
                </button>
              ))}
            </div>
            {lotteryChoice.length === 3 && (
              <p className="selected-numbers">{t('modals.gameModal.selectedNumbers', { numbers: lotteryChoice.join(', ') })}</p>
            )}
          </div>
        );

      case 'star_flip':
        return (
          <div className="starflip-interface">
            {/* Инструкция */}
            <div className="starflip-info-block">
              <div className="starflip-info-title">{t('games.starFlip.chooseSide')}:</div>
              <div className="starflip-info-sides">
                <div className="starflip-info-side moon-side">
                  <span className="info-emoji">🌙</span>
                  <span className="info-name">{t('games.starFlip.moon')}</span>
                  <span className="info-stats">55% {t('games.starFlip.chance')}</span>
                  <span className="info-boost">+30% {t('games.starFlip.for')} 3 {t('games.starFlip.min')}</span>
                </div>
                <div className="starflip-info-vs">VS</div>
                <div className="starflip-info-side star-side">
                  <span className="info-emoji">⭐</span>
                  <span className="info-name">{t('games.starFlip.star')}</span>
                  <span className="info-stats">40% {t('games.starFlip.chance')}</span>
                  <span className="info-boost">+100% {t('games.starFlip.for')} 2 {t('games.starFlip.min')}</span>
                </div>
              </div>
            </div>

            {/* Кнопки выбора */}
            <div className="starflip-buttons">
              <button
                className={`starflip-choice-btn moon ${choice === 'heads' ? 'active' : ''}`}
                onClick={() => {
                  SoundManager.playButtonClick();
                  setChoice('heads');
                }}
                disabled={playing}
              >
                <span className="choice-emoji">🌙</span>
                <span className="choice-label">{t('games.starFlip.moon')}</span>
                <span className="choice-stats">55% • +30%</span>
              </button>
              <button
                className={`starflip-choice-btn star ${choice === 'tails' ? 'active' : ''}`}
                onClick={() => {
                  SoundManager.playButtonClick();
                  setChoice('tails');
                }}
                disabled={playing}
              >
                <span className="choice-emoji">⭐</span>
                <span className="choice-label">{t('games.starFlip.star')}</span>
                <span className="choice-stats">40% • +100%</span>
              </button>
            </div>

            {/* Подсказка после выбора */}
            {choice && (
              <div className={`starflip-hint ${choice === 'heads' ? 'moon' : 'star'}`}>
                {choice === 'heads'
                  ? t('games.starFlip.moonSelected')
                  : t('games.starFlip.starSelected')}
              </div>
            )}
          </div>
        );

      case 'cosmic_dice':
        // Используем данные из API (game.config) или дефолтные значения
        const diceNumbers = game.config && Array.isArray(game.config)
          ? game.config.map((item: any) => ({
              num: item.value,
              chance: Math.round(item.chance * 100) // Конвертируем из 0.43 в 43%
            }))
          : [
            { num: 2, chance: 7 },
            { num: 3, chance: 14 },
            { num: 4, chance: 22 },
            { num: 5, chance: 29 },
            { num: 6, chance: 36 },
            { num: 7, chance: 43 },
            { num: 8, chance: 36 },
            { num: 9, chance: 29 },
            { num: 10, chance: 22 },
            { num: 11, chance: 14 },
            { num: 12, chance: 7 }
          ];

        const selectedDice = diceNumbers.find(d => d.num === choice);

        return (
          <div style={{ textAlign: 'center' }}>
            {/* Заголовок */}
            <div style={{
              fontSize: '12px',
              color: 'rgba(255,255,255,0.7)',
              marginBottom: '4px'
            }}>
              {t('games.cosmicDice.selectSum')}
            </div>

            {/* Выбранная сумма */}
            <div style={{
              fontSize: '13px',
              color: 'rgba(255,255,255,0.9)',
              marginBottom: '6px'
            }}>
              🎲 {t('modals.gameModal.sum')}: <b style={{color: '#fbbf24', fontSize: '18px'}}>{choice || '?'}</b>
              {selectedDice && <span style={{marginLeft: '6px', color: '#4ade80', fontSize: '12px'}}>({selectedDice.chance}%)</span>}
            </div>

            {/* Сетка кнопок */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(6, 1fr)',
              gap: '4px'
            }}>
              {diceNumbers.map(dice => (
                <button
                  key={dice.num}
                  onClick={() => {
                    SoundManager.playButtonClick();
                    setChoice(dice.num);
                  }}
                  disabled={playing}
                  style={{
                    background: choice === dice.num
                      ? 'linear-gradient(135deg, #fbbf24, #f59e0b)'
                      : 'rgba(255,255,255,0.1)',
                    border: choice === dice.num
                      ? '2px solid #fbbf24'
                      : '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '6px',
                    padding: '6px 8px',
                    minWidth: '42px',
                    cursor: playing ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    color: choice === dice.num ? '#1a1a2e' : '#fff',
                    opacity: playing ? 0.6 : 1,
                    transition: 'all 0.15s'
                  }}
                >
                  <span style={{fontSize: '14px', fontWeight: 700, lineHeight: 1}}>{dice.num}</span>
                  <span style={{fontSize: '8px', opacity: 0.7, lineHeight: 1}}>{dice.chance}%</span>
                </button>
              ))}
            </div>

          </div>
        );

      case 'meteor_race':
        // Используем загруженные из БД ракеты или дефолтные значения
        const activeRockets = rockets.length > 0 ? rockets : [
          { id: 1, emoji: '🚀', color: '#FF6B6B', name: t('games.meteorRace.rockets.redLightning'), chance: 60, boost: 30, duration: 45 },
          { id: 2, emoji: '🛸', color: '#4ECDC4', name: t('games.meteorRace.rockets.blueWhirlwind'), chance: 40, boost: 100, duration: 80 },
          { id: 3, emoji: '🛰️', color: '#45B7D1', name: t('games.meteorRace.rockets.spaceWanderer'), chance: 25, boost: 200, duration: 150 },
          { id: 4, emoji: '✨', color: '#FFA07A', name: t('games.meteorRace.rockets.starDust'), chance: 15, boost: 400, duration: 300 }
        ];

        const selectedRocket = activeRockets.find(r => r.id === choice);

        return (
          <div className="game-interface race">
            <h3>{t('modals.gameModal.meteorRaceTitle')}</h3>
            <p style={{fontSize: '12px', marginBottom: '4px'}}>{t('modals.gameModal.meteorRaceDesc')}</p>

            {selectedRocket && (
              <div style={{
                background: 'rgba(255,255,255,0.1)',
                padding: '8px',
                borderRadius: '8px',
                margin: '8px 0',
                fontSize: '12px',
                textAlign: 'center'
              }}>
                <div style={{color: selectedRocket.color, fontWeight: 'bold', marginBottom: '4px'}}>
                  {selectedRocket.emoji} {selectedRocket.name}
                </div>
                <div style={{color: '#FFD700', fontSize: '11px'}}>
                  {selectedRocket.chance}% {t('games.starFlip.chance')} • {selectedRocket.boost}% {t('modals.gameModal.boost')} • {selectedRocket.duration} {t('modals.gameModal.sec')}
                </div>
              </div>
            )}

            <div className="rocket-buttons">
              {activeRockets.map(rocket => (
                <button
                  key={rocket.id}
                  className={`rocket-btn ${choice === rocket.id ? 'selected' : ''}`}
                  onClick={() => {
                    SoundManager.playButtonClick();
                    setChoice(rocket.id);
                  }}
                  disabled={playing}
                  title={`${rocket.name} - ${rocket.chance}%, ${rocket.boost}%, ${rocket.duration}s`}
                  style={{
                    borderColor: choice === rocket.id ? rocket.color : 'rgba(255,255,255,0.2)'
                  }}
                >
                  <div style={{fontSize: '20px', marginBottom: '2px'}}>{rocket.emoji}</div>
                  <div style={{fontSize: '11px', fontWeight: 'bold'}}>{rocket.name}</div>
                  <div style={{fontSize: '9px', color: '#FFD700', marginTop: '2px'}}>
                    {rocket.chance}% • {rocket.boost}%
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 'asteroid_miner':
        const asteroids = [
          { id: 1, emoji: '🪨', type: 'low', name: t('modals.gameModal.typeLow') },
          { id: 2, emoji: '☄️', type: 'medium', name: t('modals.gameModal.typeMedium') },
          { id: 3, emoji: '🌑', type: 'high', name: t('modals.gameModal.typeHigh') },
          { id: 4, emoji: '💫', type: 'rare', name: t('modals.gameModal.typeRare') },
          { id: 5, emoji: '🌒', type: 'medium', name: t('modals.gameModal.typeMedium') },
          { id: 6, emoji: '⭐', type: 'low', name: t('modals.gameModal.typeLow') },
          { id: 7, emoji: '✨', type: 'high', name: t('modals.gameModal.typeHigh') },
          { id: 8, emoji: '🌠', type: 'medium', name: t('modals.gameModal.typeMedium') },
          { id: 9, emoji: '💥', type: 'low', name: t('modals.gameModal.typeLow') }
        ];

        const selectedAsteroid = asteroids.find(a => a.id === choice);

        return (
          <div className="game-interface asteroid-miner">
            <h3>{t('modals.gameModal.asteroidMinerTitle')}</h3>
            <p>{t('modals.gameModal.asteroidMinerDesc')}</p>

            {selectedAsteroid && (
              <div className="asteroid-info">
                <p className="asteroid-info-title">
                  {t('modals.gameModal.selected')} {selectedAsteroid.emoji} {selectedAsteroid.name}
                </p>
                <div className="asteroid-chances">
                  <span style={{fontSize: '11px', color: '#FFD700'}}>
                    ⚡ {t('modals.gameModal.generalWinChance')}: 40%
                  </span>
                </div>
              </div>
            )}

            <div className="asteroids-grid">
              {asteroids.map((asteroid) => (
                <button
                  key={asteroid.id}
                  className={`asteroid-btn ${asteroid.type} ${choice === asteroid.id ? 'selected' : ''}`}
                  onClick={() => {
                    SoundManager.playButtonClick();
                    setChoice(asteroid.id);
                  }}
                  disabled={playing}
                  title={`${asteroid.name} - ${t('modals.gameModal.generalWinChance')}: 40%`}
                >
                  <span className="asteroid-emoji">{asteroid.emoji}</span>
                  <span className="asteroid-potential">{asteroid.name}</span>
                </button>
              ))}
            </div>
          </div>
        );

      default:
        return (
          <div className="game-interface default">
            <h3>{getLocalizedGameName(game.id)}</h3>
            <p>{game.description}</p>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="game-modal-overlay" onClick={() => {
      SoundManager.playButtonClick();
      onClose();
    }}>
      <div className={`game-modal ${game?.id === 'cosmic_dice' ? 'dice-game' : ''}`} onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={() => {
          SoundManager.playButtonClick();
          onClose();
        }}>✕</button>
        {(game?.id === 'asteroid_miner' || game?.id === 'meteor_race' || game?.id === 'cosmic_dice' || game?.id === 'lunar_lottery' || game?.id === 'star_flip') && (
          <button className="help-btn" onClick={() => {
            SoundManager.playButtonClick();
            setShowRulesModal(true);
          }}>?</button>
        )}
        
        {loading ? (
          <div className="loading">
            <div className="spinner">🌙</div>
            <p>{t('modals.gameModal.loading')}</p>
          </div>
        ) : game ? (
          <>
            <div className="game-header">
              <h2>{getLocalizedGameName(game.id)}</h2>
              {game.id !== 'cosmic_dice' && game.id !== 'asteroid_miner' && game.id !== 'meteor_race' && game.id !== 'star_flip' && game.id !== 'lunar_lottery' && (
                <div className="game-stats">
                  <span>⚡ {t('modals.gameModal.winBoost')}</span>
                  <span>📊 {t('modals.gameModal.chance')}: {typeof game.winChance === 'number' ? (game.winChance * 100).toFixed(0) : '0'}%</span>
                </div>
              )}
            </div>


            {renderGameInterface()}

            {result && !result.loading && (() => {
              console.log('🎮 [GameModal] ========= НАЧИНАЕМ РЕНДЕР РЕЗУЛЬТАТА =========');
              console.log('🎮 [GameModal] result объект:', result);
              console.log('🎮 [GameModal] result.won:', result.won, 'тип:', typeof result.won);
              console.log('🎮 [GameModal] result.loading:', result.loading);
              console.log('🎮 [GameModal] result.boost:', result.boost);
              console.log('🎮 [GameModal] Условие result.won ? "win" : "lose" даст класс:', result.won ? 'win' : 'lose');

              const victoryText = t('modals.gameModal.victory');
              const boostActivatedText = t('modals.gameModal.boostActivated');
              const profitText = t('modals.gameModal.profit');

              console.log('🎮 [GameModal] Переводы:', {
                victory: victoryText,
                boostActivated: boostActivatedText,
                profit: profitText
              });

              return (
                <div className={`game-result ${result.won ? 'win' : 'lose'}`}>
                  {(() => {
                    console.log('🎮 [GameModal] Рендерим блок победы/поражения. result.won =', result.won);
                    return result.won ? (
                    <>
                      <h3>🎊 {victoryText}</h3>
                    {result.boost && typeof result.boost.multiplier === 'number' && typeof result.boost.duration === 'number' && (
                      <div className="boost-activated">
                        <div className="boost-icon">⚡</div>
                        <div className="boost-details">
                          <p className="boost-title">{t('modals.gameModal.boostActivated')}</p>
                          <p className="boost-multiplier">{t('modals.gameModal.profit')} +{((result.boost.multiplier - 1) * 100).toFixed(0)}%</p>
                          <p className="boost-duration">{t('modals.gameModal.for')} {result.boost.duration} {t('modals.gameModal.sec')}</p>
                        </div>
                      </div>
                    )}
                    {result.boost && typeof result.boost.multiplier === 'number' ? (
                      <p className="boost-hint">{t('modals.gameModal.incomeIncreased')} +{((result.boost.multiplier - 1) * 100).toFixed(0)}%</p>
                    ) : (
                      <p className="boost-hint">{victoryText}</p>
                    )}
                  </>
                ) : (
                  <>
                    <h3>😔 {t('modals.gameModal.noLuck')}</h3>
                    <p>{t('modals.gameModal.tryAgain')}</p>
                  </>
                );
                  })()}
                </div>
              );
            })()}
            
            {result && result.loading && (
              <div className="game-progress">
                <div className="progress-animation">
                  <div className="dice-rolling">🎲</div>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{width: '100%'}}></div>
                  </div>
                  <p>{t('modals.gameModal.playing')}</p>
                </div>
              </div>
            )}

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {/* Информация об активном бусте */}
            {attemptInfo?.activeBoost?.active && (
              <div className="active-boost-warning">
                <div>⚡ {t('modals.gameModal.boostActive')}: x{attemptInfo.activeBoost.multiplier} {Math.floor((attemptInfo.activeBoost.remainingSeconds || 0) / 60)}:{String((attemptInfo.activeBoost.remainingSeconds || 0) % 60).padStart(2, '0')}</div>
                <div style={{fontSize: '11px', opacity: 0.8}}>{t('modals.gameModal.waitForBoost')}</div>
              </div>
            )}

            {/* Кнопка игры - скрываем после победы */}
            {!(result && result.won) && (
              <div className="game-controls">
                {attemptInfo?.activeBoost?.active ? (
                  <button className="play-btn" disabled>
                    ⚡ {t('modals.gameModal.boostActiveWait')}
                  </button>
                ) : attemptInfo && attemptInfo.maxAttemptsReached ? (
                  <button className="play-btn" disabled>
                    {t('modals.gameModal.limitReached')}
                  </button>
                ) : !game.canPlay && game.cooldownLeft > 0 ? (
                  <button className="play-btn" disabled>
                    {t('modals.gameModal.availableIn', { time: formatCooldown(game.cooldownLeft) })}
                  </button>
                ) : attemptInfo && !attemptInfo.canAfford && !attemptInfo.isFreeAttempt ? (
                  <button className="play-btn" disabled>
                    {t('modals.gameModal.insufficientSperm')}
                  </button>
                ) : (
                  <button
                    className="play-btn"
                    onClick={playGame}
                    disabled={playing || !choice || (game.id === 'lunar_lottery' && choice?.length !== 3)}
                  >
                    {playing
                      ? t('modals.gameModal.playing')
                      : !choice && game.id === 'asteroid_miner'
                        ? t('modals.gameModal.selectAsteroid')
                        : !choice && game.id === 'lunar_lottery'
                        ? t('modals.gameModal.selectNumbers')
                        : !choice && game.id === 'star_flip'
                        ? t('modals.gameModal.selectSide')
                        : !choice && game.id === 'cosmic_dice'
                        ? t('modals.gameModal.selectDiceSum')
                        : !choice && game.id === 'meteor_race'
                        ? t('modals.gameModal.selectRocket')
                        : (game.id === 'lunar_lottery' && choice?.length !== 3)
                        ? t('modals.gameModal.selectedNumbersCount', { count: choice?.length || 0 })
                        : attemptInfo?.isFreeAttempt
                        ? t('modals.gameModal.playFree')
                        : t('modals.gameModal.playFor', { cost: attemptInfo?.nextAttemptCost ?? game.cost })}
                  </button>
                )}
              </div>
            )}

            {/* Компактная карточка с балансом и попытками */}
            <div className="game-info-compact">
              {attemptInfo && (
                <>
                  <div className="info-line">
                    <span>💰 {user?.balance || 0}</span>
                    <span>•</span>
                    <span>🎯 {attemptInfo.freeAttemptsRemaining}/{attemptInfo.freeAttemptsPerDay} {t('modals.gameModal.freeAttemptsShort')}</span>
                    <span>•</span>
                    <span>📊 {attemptInfo.totalAttemptsToday} {t('modals.gameModal.playedShort')}</span>
                  </div>
                  {attemptInfo.freeAttemptsRemaining === 0 && !attemptInfo.maxAttemptsReached && (
                    <div className="info-line secondary">
                      <span>{t('modals.gameModal.paidAttempts')} {attemptInfo.paidAttemptsToday}/{attemptInfo.maxPaidAttempts || 6}</span>
                      <span>•</span>
                      <span>{t('modals.gameModal.price')} {attemptInfo.nextAttemptCost} SPERM</span>
                    </div>
                  )}
                  {attemptInfo.maxAttemptsReached && (
                    <div className="info-line warning">
                      {t('modals.gameModal.limitReached')}
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        ) : (
          <div className="error">
            <p>{t('modals.gameModal.gameNotFound')}</p>
          </div>
        )}

        {/* Модалка с правилами игры (модалка в модалке) */}
        {showRulesModal && (
          <div className="rules-modal-overlay" onClick={() => setShowRulesModal(false)}>
            <div className="rules-modal" onClick={(e) => e.stopPropagation()}>
              <button className="rules-close-btn" onClick={() => {
                SoundManager.playButtonClick();
                setShowRulesModal(false);
              }}>✕</button>

              <div className="rules-modal-content">
                {game?.id === 'lunar_lottery' ? (
                  <>
                    <h3 className="rules-modal-title">{t('modals.gameModal.rules.lunarLottery.title')}</h3>

                    <p className="rules-main">{t('modals.gameModal.rules.lunarLottery.main')}</p>

                    <div className="rules-rewards">
                      <div className="reward-item jackpot">{t('modals.gameModal.rules.lunarLottery.jackpot')}</div>
                      <div className="reward-item great">{t('modals.gameModal.rules.lunarLottery.great')}</div>
                      <div className="reward-item good">{t('modals.gameModal.rules.lunarLottery.good')}</div>
                      <div className="reward-item empty">{t('modals.gameModal.rules.lunarLottery.empty')}</div>
                    </div>

                    <p className="rules-strategy">
                      <strong>{t('common.strategy')}:</strong> {t('modals.gameModal.rules.lunarLottery.strategy')}
                    </p>

                    <p className="rules-note">
                      ⚠️ <strong>{t('common.important')}:</strong> {t('modals.gameModal.rules.lunarLottery.note')}
                    </p>
                  </>
                ) : game?.id === 'star_flip' ? (
                  <>
                    <h3 className="rules-modal-title">{t('modals.gameModal.rules.starFlip.title')}</h3>

                    <p className="rules-main">{t('modals.gameModal.rules.starFlip.main')}</p>

                    <div className="rules-rewards">
                      <div className="reward-item good">{t('modals.gameModal.rules.starFlip.moon')}</div>
                      <div className="reward-item jackpot">{t('modals.gameModal.rules.starFlip.star')}</div>
                    </div>

                    <p className="rules-strategy">
                      <strong>{t('common.strategy')}:</strong> {t('modals.gameModal.rules.starFlip.strategy')}
                    </p>

                    <p className="rules-note">
                      ⚠️ <strong>{t('common.important')}:</strong> {t('modals.gameModal.rules.starFlip.note')}
                    </p>
                  </>
                ) : game?.id === 'cosmic_dice' ? (
                  <>
                    <h3 className="rules-modal-title">{t('modals.gameModal.rules.cosmicDice.title')}</h3>

                    <p className="rules-main">{t('modals.gameModal.rules.cosmicDice.main')}</p>

                    <div className="rules-rewards">
                      <div className="reward-item jackpot">{t('modals.gameModal.rules.cosmicDice.sum2or12')}</div>
                      <div className="reward-item great">{t('modals.gameModal.rules.cosmicDice.sum3or11')}</div>
                      <div className="reward-item good">{t('modals.gameModal.rules.cosmicDice.sum4or10')}</div>
                      <div className="reward-item small">{t('modals.gameModal.rules.cosmicDice.sum7')}</div>
                    </div>

                    <p className="rules-strategy">
                      <strong>{t('common.strategy')}:</strong> {t('modals.gameModal.rules.cosmicDice.strategy')}
                    </p>

                    <p className="rules-note">
                      ⚠️ <strong>{t('common.important')}:</strong> {t('modals.gameModal.rules.cosmicDice.note')}
                    </p>
                  </>
                ) : game?.id === 'meteor_race' ? (
                  <>
                    <h3 className="rules-modal-title">{t('modals.gameModal.rules.meteorRace.title')}</h3>

                    <p className="rules-main">{t('modals.gameModal.rules.meteorRace.main')}</p>

                    <div className="rules-rewards">
                      <div className="reward-item small">{t('modals.gameModal.rules.meteorRace.redLightning')}</div>
                      <div className="reward-item good">{t('modals.gameModal.rules.meteorRace.blueWhirl')}</div>
                      <div className="reward-item great">{t('modals.gameModal.rules.meteorRace.spaceWanderer')}</div>
                      <div className="reward-item jackpot">{t('modals.gameModal.rules.meteorRace.starDust')}</div>
                    </div>

                    <p className="rules-strategy">
                      <strong>{t('common.strategy')}:</strong> {t('modals.gameModal.rules.meteorRace.strategy')}
                    </p>

                    <p className="rules-note">
                      ⚠️ <strong>{t('common.important')}:</strong> {t('modals.gameModal.rules.meteorRace.note')}
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="rules-modal-title">{t('modals.gameModal.rules.asteroidMiner.title')}</h3>

                    <p className="rules-main">{t('modals.gameModal.rules.asteroidMiner.main')}</p>

                    <div className="rules-rewards">
                      <div className="reward-item jackpot">{t('modals.gameModal.rules.asteroidMiner.jackpot')}</div>
                      <div className="reward-item good">{t('modals.gameModal.rules.asteroidMiner.goodFind')}</div>
                      <div className="reward-item small">{t('modals.gameModal.rules.asteroidMiner.smallFind')}</div>
                      <div className="reward-item empty">{t('modals.gameModal.rules.asteroidMiner.emptyFind')}</div>
                    </div>

                    <p className="rules-strategy">
                      <strong>{t('common.strategy')}:</strong> {t('modals.gameModal.rules.asteroidMiner.strategy')}
                    </p>

                    <p className="rules-note">
                      ⚠️ <strong>{t('common.important')}:</strong> {t('modals.gameModal.rules.asteroidMiner.note')}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameModal;